import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { Send, Loader2 } from 'lucide-react'
import { apiUrl } from '../lib/api'

interface ContactFormValues {
  name: string
  email: string
  subject: string
  message: string
  company?: string
}

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i

const labelClass = 'mono-label block mb-1.5'

const ContactForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({ mode: 'onBlur' })

  const onSubmit = async (values: ContactFormValues) => {
    if (values.company) return
    try {
      const response = await fetch(apiUrl('/api/contact'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: values.name,
          email: values.email,
          subject: values.subject,
          message: values.message,
        }),
      })

      if (response.ok) {
        toast.success("Message sent — I'll get back to you soon.")
        reset()
        return
      }

      const payload = (await response.json().catch(() => null)) as {
        detail?: string
      } | null
      toast.error(
        payload?.detail ?? 'Something went wrong. Please try again.'
      )
    } catch {
      toast.error("Couldn't reach the server — please try again later.")
    }
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="flex flex-col gap-6"
    >
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className={labelClass}>
            Name
          </label>
          <input
            id="contact-name"
            type="text"
            autoComplete="name"
            placeholder="Your name"
            aria-invalid={errors.name ? 'true' : undefined}
            className="field"
            {...register('name', {
              required: 'Please enter your name',
            })}
          />
          {errors.name && (
            <p role="alert" className="mt-1.5 text-xs text-red-500 dark:text-red-400">
              {errors.name.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="contact-email" className={labelClass}>
            Email
          </label>
          <input
            id="contact-email"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            aria-invalid={errors.email ? 'true' : undefined}
            className="field"
            {...register('email', {
              required: 'Please enter your email',
              pattern: {
                value: EMAIL_REGEX,
                message: 'Enter a valid email address',
              },
            })}
          />
          {errors.email && (
            <p role="alert" className="mt-1.5 text-xs text-red-500 dark:text-red-400">
              {errors.email.message}
            </p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="contact-subject" className={labelClass}>
          Subject
        </label>
        <input
          id="contact-subject"
          type="text"
          placeholder="What is this about?"
          aria-invalid={errors.subject ? 'true' : undefined}
          className="field"
          {...register('subject', {
            required: 'Please add a subject',
          })}
        />
        {errors.subject && (
          <p role="alert" className="mt-1.5 text-xs text-red-500 dark:text-red-400">
            {errors.subject.message}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="contact-message" className={labelClass}>
          Message
        </label>
        <textarea
          id="contact-message"
          rows={4}
          placeholder="Tell me about the role, project or infrastructure problem…"
          aria-invalid={errors.message ? 'true' : undefined}
          className="field resize-none"
          {...register('message', {
            required: 'Please write a short message',
            minLength: {
              value: 10,
              message: 'Message should be at least 10 characters',
            },
          })}
        />
        {errors.message && (
          <p role="alert" className="mt-1.5 text-xs text-red-500 dark:text-red-400">
            {errors.message.message}
          </p>
        )}
      </div>

      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        {...register('company')}
      />

      <button
        type="submit"
        disabled={isSubmitting}
        className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isSubmitting ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Sending…
          </>
        ) : (
          <>
            <Send size={16} />
            Send message
          </>
        )}
      </button>
    </form>
  )
}

export default ContactForm